import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { SelectProps } from 'semantic-ui-react';
import { Validation } from '../../../model/validation/validation';
import { ValidationNotEmpty } from '../../../model/validation/validation-not-empty';
import { UserAgent } from '../../../utilities/user-agent';
import { ErrorPop } from '../error-pop/error-pop';
import { Required } from '../required/required';
import './select.scss';

type Props = {
  validationList?: Validation[];
  /**  触る前からエラー出すかどうか */
  touch?: boolean;
  noRequireLabel?:boolean;
  require?: boolean;
  defaultLabel?: string;
  onChange: (v: string | number, text: string) => void;
  id?: string;
  label?: string;
  value?: number | string;
  /** 任意文字列の場合string Valueの場合true */
  title?: string | boolean;
  options: { text: string, value: string | number; }[]
  errorPosBottom?: boolean;
  disabled?: boolean;
  errorMessage?: string[],
  changeRequire?: boolean;
} & Omit<SelectProps, 'onChange' | 'options'>

export const Select = (props: Props) => {
  const {
    validationList,
    require,
    touch,
    value,
    disabled,
    options,
    onChange,
    defaultLabel,
    className,
    id,
    title,
    label,
    noRequireLabel,
    errorPosBottom,
    errorMessage,
    changeRequire,
  } = props;

  /* State */
  const [focus, setFocus] = useState<boolean>(false);
  const [errorList, setErrorList] = useState<string[]>([]);
  const [changed, setChanged] = useState<boolean>(false);
  const error = Boolean((touch || changed) && errorList.length);

  /* Memo */
  const tip = useMemo(() => {
    if (!title) return undefined;
    if (typeof title === 'string') {
      return title;
    }
    if (!value) {
      return options[0].text;
    }
    return title ? options.find((v) => v.value === value)?.text || '' : '';
  }, [title, value, options]);

  /* Callback */
  const handleOnChange = useCallback((val: string | number) => {
    if (!options.length) return;
    const text = options.find((v) => v.value === val)?.text || '';
    setChanged(true);
    setFocus(false);
    if (onChange) {
      if (typeof val === 'string') {
        if (typeof value === 'string') {
          onChange(val ? String(val) : '', text);
        } else {
          onChange(val ? Number(val) : NaN, text);
        }
      } else {
        onChange(val ? Number(val) : NaN, text);
      }
    }
    if (!validationList) {
      setErrorList(
        require ? ValidationNotEmpty
          .filter((v) => !v.run(val ? String(val) : ''))
          .map((v) => v.errorMessage)
          : [],
      );
    } else {
      setErrorList(
        require ? [...ValidationNotEmpty, ...validationList]
          .filter((v) => !v.run(val ? String(val) : ''))
          .map((v) => v.errorMessage)
          : validationList
            .filter((v) => !v || !v.run(val ? String(val) : ''))
            .map((v) => v.errorMessage),
      );
    }
  },
  [changed, onChange, validationList, require, options, value]);

  // const handleOnChange = useCallback(
  //   (event: globalThis.React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
  //     setChanged(true);
  //     if (onChange) {
  //       onChange(data.value as string | number);
  //     }
  //   },
  //   [changed, onChange],
  // );

  // eslint-disable-next-line
  const handleOnBlur = useCallback(
    (bv: string) => {
      if (!options.length) return;
      setChanged(true);
      setFocus(false);
      if (!validationList) {
        setErrorList(
          require ? ValidationNotEmpty
            .filter((v) => !v.run(bv ? String(bv) : ''))
            .map((v) => v.errorMessage)
            : [],
        );
      } else {
        setErrorList(
          require ? [...ValidationNotEmpty, ...validationList]
            .filter((v) => !v.run(bv ? String(bv) : ''))
            .map((v) => v.errorMessage)
            : validationList
              .filter((v) => !v || !v.run(bv ? String(bv) : ''))
              .map((v) => v.errorMessage),
        );
      }
    },
    [validationList, options],
  );
  // const handleOnBlur = useCallback(
  //   (_:any, data: DropdownProps) => {
  //     setChanged(true);
  //     setFocus(false);
  //     if (!validationList) {
  //       setErrorList(
  //         require ? ValidationNotEmpty
  //           .filter((v) => !v.run(data.value ? String(data.value) : ''))
  //           .map((v) => v.errorMessage)
  //           : [],
  //       );
  //     } else {
  //       setErrorList(
  //         require ? [...ValidationNotEmpty, ...validationList]
  //           .filter((v) => !v.run(data.value ? String(data.value) : ''))
  //           .map((v) => v.errorMessage)
  //           : validationList
  //             .filter((v) => !v || !v.run(data.value ? String(data.value) : ''))
  //             .map((v) => v.errorMessage),
  //       );
  //     }
  //   },
  //   [],
  // );

  useEffect(() => {
    if (touch) {
      if (!validationList) {
        setErrorList(
          require ? ValidationNotEmpty
            .filter((v) => !v.run(value ? String(value) : ''))
            .map((v) => v.errorMessage)
            : [],
        );
      } else {
        setErrorList(
          require ? [...ValidationNotEmpty, ...validationList]
            .filter((v) => !v.run(value ? String(value) : ''))
            .map((v) => v.errorMessage)
            : validationList
              .filter((v) => !v.run(value ? String(value) : ''))
              .map((v) => v.errorMessage),
        );
      }
    }
  }, [touch]);

  useEffect(() => {
    if (changeRequire && disabled) {
      if (!require) {
        setErrorList([]);
      }
    }
  }, [require]);

  const formValue = useMemo(() => {
    if (typeof value === 'string') {
      return value || '';
    }
    return value ? Number(value) : 0;
  }, [value]);

  return (
    <div
      className={`pulldown_select ${className || ''}`}
      title={tip}
      onFocus={() => setFocus(true)}
    >
      <label className="for_select">
        <span>{label}</span>{label && require && !noRequireLabel && <Required />}
        <div className={`selector ${(error || errorMessage?.length) ? 'error' : ''}`}>
          <select
            id={id}
            disabled={disabled}
            onBlur={(e) => { handleOnBlur(e.target.value); }}
            onChange={(e) => { handleOnChange(e.target.value); }}
            value={formValue}
            className={`${UserAgent === 'pc' ? ' error_focus' : ''}`}
          >
            {defaultLabel && <option key="default" value="">{defaultLabel}</option>}
            {options.map((v, i) => (
              <option key={`${v.text}${i}`} value={v.value}>{v.text}</option>
            ))}
          </select>
          {(error || !!errorMessage?.length) && (UserAgent === 'sp' ? focus : true) && (
          <ErrorPop
            messages={errorMessage || errorList}
            errorPosBottom={errorPosBottom}
          />
          )}

        </div>
        {/* {UserAgent === 'pc'
        && (
        <SSelect
          id={id}
          error={error}
          value={value ?? NaN}
          disabled={disabled}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          placeholder={placeholder}
          options={defaultLabel ? [
            { text: defaultLabel, value: NaN },
            ...options,
          ] : options}
        />
        )} */}
      </label>
    </div>
  );
};
